import { useState } from 'react'
import { ActionIcon, Badge, Button, Group, Text } from '@mantine/core'
import { IconPencil, IconTrash } from '@tabler/icons-react'
import { FundGroup } from 'app/models/fund-group'
import { FUND_GROUP_COPY } from './fund-group-copy'
import { formatCreated } from './fund-group-utils'

type Props = {
  group: FundGroup
  entityNames: string[]
  onEdit: () => void
  onDelete: () => Promise<boolean>
}

/** One row of the manage list. Delete asks for confirmation INLINE on the card, per the story. */
export function FundGroupCard({ group, entityNames, onEdit, onDelete }: Props) {
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const confirmDelete = async () => {
    setDeleting(true)
    const ok = await onDelete()
    setDeleting(false)
    if (!ok) setConfirming(false)
  }

  return (
    <div
      className={`fund-group-card${confirming ? ' is-confirming' : ''}`}
      data-testid={`fund-group-card-${group.id}`}
    >
      <div className="fund-group-card-row">
        <div className="fund-group-card-main">
          <Group spacing={10} align="center">
            <Text className="fund-group-card-name">{group.name}</Text>
            <Badge
              className={`fund-group-badge fund-group-badge-${group.visibility.toLowerCase()}`}
              variant="filled"
              radius="xl"
            >
              {group.visibility}
            </Badge>
          </Group>
          <Group spacing={30} className="fund-group-card-meta">
            <Text>{FUND_GROUP_COPY.entityCount(group.entityIds.length)}</Text>
            <Text>
              {FUND_GROUP_COPY.created(formatCreated(group.createdAt))}
            </Text>
          </Group>
          <Group spacing={10} className="fund-group-card-chips">
            {entityNames.map(name => (
              <span key={name} className="fund-group-chip">
                {name}
              </span>
            ))}
          </Group>
        </div>
        <div className="fund-group-card-actions">
          <Group spacing={20} noWrap>
            <ActionIcon
              aria-label={`Edit ${group.name}`}
              variant="transparent"
              onClick={onEdit}
              disabled={deleting}
            >
              <IconPencil size={22} color="#32439a" />
            </ActionIcon>
            <ActionIcon
              aria-label={`Delete ${group.name}`}
              variant="transparent"
              onClick={() => setConfirming(true)}
              disabled={deleting}
            >
              <IconTrash size={22} color="#ee5959" />
            </ActionIcon>
          </Group>
        </div>
      </div>

      {/* Inline confirmation, below the card content (product mock). */}
      {confirming && (
        <div
          className="fund-group-card-confirm"
          role="alertdialog"
          aria-label={FUND_GROUP_COPY.deleteConfirm(group.name)}
        >
          <Text className="fund-group-card-confirm-text">
            {FUND_GROUP_COPY.deleteConfirm(group.name)}
          </Text>
          <Group spacing={14}>
            <Button
              className="fund-group-confirm-delete"
              loading={deleting}
              onClick={confirmDelete}
            >
              {FUND_GROUP_COPY.delete}
            </Button>
            <Button
              className="fund-group-confirm-cancel"
              variant="default"
              disabled={deleting}
              onClick={() => setConfirming(false)}
            >
              {FUND_GROUP_COPY.cancel}
            </Button>
          </Group>
        </div>
      )}
    </div>
  )
}
